import { BRANDS, GOOGLE_FAMILY, GOOGLE_G, RASTER_BRANDS } from './brandMarks';
import { isMonoHex } from '../lib/css';
import { BrandMark } from './BrandMark';

/** Whether a real brand logo exists for this catalog platform id. */
export const hasBrandIcon = (id: string): boolean =>
  id === 'anyapi' || id in BRANDS || id in RASTER_BRANDS || GOOGLE_FAMILY.has(id);

/** The brand's own hex, whichever kind of mark carries it. */
export const brandHex = (id: string): string | undefined =>
  BRANDS[id]?.hex ?? RASTER_BRANDS[id]?.hex;

/** Whether this brand's own color is too low-contrast to render on a faint tint
 *  of itself (near-black marks vanish on the dark theme; very light ones on the
 *  light theme) - those defer to the surrounding `currentColor` instead. */
export const isMonoBrand = (id: string): boolean => {
  if (id === 'anyapi') return true;
  if (id === 'google' || GOOGLE_FAMILY.has(id)) return true;
  const mark = BRANDS[id];
  if (mark?.backgroundPath) return false;
  return !!mark && isMonoHex(mark.hex);
};

export interface BrandGlyphProps {
  /** catalog platform id, e.g. `tiktok`, `x-twitter` */
  id: string;
  /** glyph size in px (default 22) */
  size?: number;
  /** extra classes on the svg */
  className?: string;
}

/** Just the brand logo glyph (no chip box). Vivid brands render in their own
 *  brand color; monochrome marks (X, TikTok, Google G) use `currentColor` so the
 *  caller can theme them. Returns null when no mark exists for `id`. */
export const BrandGlyph = ({ id, size = 22, className }: BrandGlyphProps) => {
  if (id === 'anyapi') {
    return (
      <span role="img" aria-label="AnyAPI" className={className}>
        <BrandMark size={size} />
      </span>
    );
  }
  if (id === 'google' || GOOGLE_FAMILY.has(id)) {
    return (
      <svg
        role="img"
        aria-label="Google"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        className={className}
      >
        {GOOGLE_G.map((p) => (
          <path key={p.fill} d={p.d} fill={p.fill} />
        ))}
      </svg>
    );
  }
  // Raster brands are full-color artwork, so they never take `currentColor`.
  const raster = RASTER_BRANDS[id];
  if (raster) {
    return (
      <img
        src={raster.src}
        alt={raster.title}
        width={size}
        height={size}
        className={className}
        style={{ objectFit: 'contain' }}
      />
    );
  }
  const mark = BRANDS[id];
  if (!mark) return null;
  return (
    <svg
      role="img"
      aria-label={mark.title}
      viewBox={mark.viewBox ?? '0 0 24 24'}
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      style={isMonoBrand(id) ? undefined : { color: `#${mark.hex}` }}
    >
      {mark.backgroundPath && <path d={mark.backgroundPath} />}
      <path
        d={mark.path}
        fill={mark.pathHex ? `#${mark.pathHex}` : undefined}
        fillRule={mark.fillRule}
      />
    </svg>
  );
};

export interface BrandIconChipProps {
  /** catalog platform id, e.g. `tiktok`, `x-twitter` */
  id: string;
  /** chip box size in px (default 38) */
  size?: number;
  /** logo glyph size in px (default 19) */
  iconSize?: number;
  /** corner radius in px (default 10) */
  radius?: number;
  /** extra classes appended to the chip box */
  className?: string;
}

/** A rounded chip showing a platform's real brand logo. Vivid brands render in
 *  their brand color over a faint tint of it; monochrome marks (X, TikTok) fall
 *  back to the theme foreground so they stay legible in both light and dark. */
export const BrandIconChip = ({
  id,
  size = 38,
  iconSize = 19,
  radius = 10,
  className,
}: BrandIconChipProps) => {
  if (!hasBrandIcon(id)) return null;
  // Vivid brands sit in their brand color over a faint tint of it. Monochrome
  // marks (X, GitHub, Snapchat yellow, the Google G) read as too low-contrast on
  // their own tint - near-black ones vanish on the dark theme - so they render
  // in the theme foreground over a neutral surface instead.
  const mono = isMonoBrand(id);
  const hex = brandHex(id);
  const tint = mono || !hex ? undefined : `#${hex}1a`;
  return (
    <div
      className={`grid place-items-center${mono ? ' bg-surface-2 text-text' : ''}${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size, borderRadius: radius, background: tint }}
    >
      <BrandGlyph id={id} size={iconSize} />
    </div>
  );
};
